import { useState } from 'react';
import { useBottomSheet } from '@/hooks/useBottomSheet';

interface TicketListModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Ticket {
  id: string;
  number: string;
  subject: string;
  category: string;
  priority: 'baixa' | 'media' | 'alta' | 'urgente';
  status: 'aberto' | 'em-andamento' | 'aguardando' | 'resolvido' | 'fechado';
  createdAt: Date;
  updatedAt: Date;
  messages: number;
}

export const TicketListModal: React.FC<TicketListModalProps> = ({ isOpen, onClose }) => {
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [filter, setFilter] = useState<string>('todos');

  const { mounted, sheetClass, backdropClass, handleClose: animatedClose } = useBottomSheet({ isOpen, onClose });

  if (!mounted) return null;

  const mockTickets: Ticket[] = [
    {
      id: '1',
      number: 'TK123456',
      subject: 'Erro ao carregar dashboard de pontos',
      category: 'Suporte Técnico',
      priority: 'alta',
      status: 'em-andamento',
      createdAt: new Date('2024-01-15T10:30:00'),
      updatedAt: new Date('2024-01-15T14:20:00'),
      messages: 3
    },
    {
      id: '2',
      number: 'TK123455',
      subject: 'Dúvida sobre resgate de pontos',
      category: 'Financeiro',
      priority: 'media',
      status: 'aguardando',
      createdAt: new Date('2024-01-14T09:15:00'),
      updatedAt: new Date('2024-01-14T16:45:00'),
      messages: 5
    },
    {
      id: '3',
      number: 'TK123454',
      subject: 'Solicitação de alteração de cadastro',
      category: 'Cadastro',
      priority: 'baixa',
      status: 'resolvido',
      createdAt: new Date('2024-01-13T11:20:00'),
      updatedAt: new Date('2024-01-13T15:30:00'),
      messages: 2
    },
    {
      id: '4',
      number: 'TK123453',
      subject: 'Sistema não está registrando missões',
      category: 'Gamificação',
      priority: 'urgente',
      status: 'aberto',
      createdAt: new Date('2024-01-15T08:00:00'),
      updatedAt: new Date('2024-01-15T08:00:00'),
      messages: 1
    },
    {
      id: '5',
      number: 'TK123452',
      subject: 'Relatório de campanhas não exporta',
      category: 'Relatórios',
      priority: 'media',
      status: 'fechado',
      createdAt: new Date('2024-01-12T14:30:00'),
      updatedAt: new Date('2024-01-12T17:00:00'),
      messages: 4
    }
  ];

  const getPriorityColor = (priority: string) => {
    const colors = {
      baixa: 'bg-gray-100 text-gray-700',
      media: 'bg-blue-100 text-blue-700',
      alta: 'bg-orange-100 text-orange-700',
      urgente: 'bg-red-100 text-red-700'
    };
    return colors[priority as keyof typeof colors] || colors.media;
  };

  const getStatusColor = (status: string) => {
    const colors = {
      'aberto': 'bg-blue-100 text-blue-700',
      'em-andamento': 'bg-yellow-100 text-yellow-700',
      'aguardando': 'bg-purple-100 text-purple-700',
      'resolvido': 'bg-green-100 text-green-700',
      'fechado': 'bg-gray-100 text-gray-700'
    };
    return colors[status as keyof typeof colors] || colors.aberto;
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      'aberto': 'Aberto',
      'em-andamento': 'Em Andamento',
      'aguardando': 'Aguardando',
      'resolvido': 'Resolvido',
      'fechado': 'Fechado'
    };
    return labels[status as keyof typeof labels] || status;
  };

  const filteredTickets = filter === 'todos' 
    ? mockTickets 
    : mockTickets.filter(ticket => ticket.status === filter);

  if (selectedTicket) {
    return (
      <div className={`fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4 ${backdropClass}`} onClick={animatedClose}>
        <div className={`bg-white rounded-t-2xl sm:rounded-xl shadow-2xl max-w-3xl w-full overflow-y-auto ${sheetClass}`} style={{ maxHeight: 'calc(100dvh - 64px)' }} onClick={(e) => e.stopPropagation()}>
          <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setSelectedTicket(null)}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                <span className="text-sm font-medium whitespace-nowrap">Voltar</span>
              </button>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-gray-500 text-xl"></i>
              </button>
            </div>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm text-gray-500 mb-1">Chamado #{selectedTicket.number}</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">{selectedTicket.subject}</h2>
                <div className="flex flex-wrap gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getStatusColor(selectedTicket.status)}`}>
                    {getStatusLabel(selectedTicket.status)}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getPriorityColor(selectedTicket.priority)}`}>
                    Prioridade: {selectedTicket.priority.charAt(0).toUpperCase() + selectedTicket.priority.slice(1)}
                  </span>
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold whitespace-nowrap">
                    {selectedTicket.category}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6 pb-20 sm:pb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-500 mb-1">Criado em</div>
                  <div className="font-semibold text-gray-900">
                    {selectedTicket.createdAt.toLocaleDateString('pt-BR')} às {selectedTicket.createdAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 mb-1">Última atualização</div>
                  <div className="font-semibold text-gray-900">
                    {selectedTicket.updatedAt.toLocaleDateString('pt-BR')} às {selectedTicket.updatedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Histórico de Mensagens</h3>
              <div className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-600">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                        <i className="ri-user-line text-white text-sm"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">Você</div>
                        <div className="text-xs text-gray-500">{selectedTicket.createdAt.toLocaleString('pt-BR')}</div>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 text-sm">
                    Olá, estou com problemas ao acessar o dashboard de pontos. Quando tento carregar a página, aparece uma mensagem de erro. Poderiam verificar?
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                        <i className="ri-customer-service-2-line text-white text-sm"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">Suporte - Carlos Santos</div>
                        <div className="text-xs text-gray-500">{new Date(selectedTicket.createdAt.getTime() + 3600000).toLocaleString('pt-BR')}</div>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 text-sm">
                    Olá! Obrigado por entrar em contato. Já identificamos o problema e nossa equipe técnica está trabalhando na solução. Você pode me informar qual navegador está utilizando?
                  </p>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-600">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                        <i className="ri-user-line text-white text-sm"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">Você</div>
                        <div className="text-xs text-gray-500">{new Date(selectedTicket.createdAt.getTime() + 7200000).toLocaleString('pt-BR')}</div>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 text-sm">
                    Estou usando o Google Chrome, versão mais recente.
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Adicionar Resposta</h3>
              <textarea
                placeholder="Digite sua mensagem..."
                rows={4}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
              ></textarea>
              <div className="flex justify-end mt-3">
                <button className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer font-semibold whitespace-nowrap">
                  Enviar Resposta
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4 ${backdropClass}`} onClick={animatedClose}>
      <div className={`bg-white rounded-t-2xl sm:rounded-xl shadow-2xl max-w-5xl w-full overflow-hidden flex flex-col ${sheetClass}`} style={{ maxHeight: 'calc(100dvh - 64px)' }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <i className="ri-file-list-3-line text-purple-600 text-xl"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Meus Chamados</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-500 text-xl"></i>
          </button>
        </div>

        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-wrap gap-2">
            {['todos', 'aberto', 'em-andamento', 'aguardando', 'resolvido', 'fechado'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  filter === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status === 'todos' ? 'Todos' : getStatusLabel(status)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 pb-20 sm:pb-6">
          <div className="space-y-3">
            {filteredTickets.map((ticket) => (
              <div
                key={ticket.id}
                onClick={() => setSelectedTicket(ticket)}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-sm font-mono text-gray-500">#{ticket.number}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${getStatusColor(ticket.status)}`}>
                        {getStatusLabel(ticket.status)}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{ticket.subject}</h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center space-x-1">
                        <i className="ri-folder-line"></i>
                        <span className="whitespace-nowrap">{ticket.category}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <i className="ri-message-3-line"></i>
                        <span className="whitespace-nowrap">{ticket.messages} mensagens</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <i className="ri-time-line"></i>
                        <span className="whitespace-nowrap">
                          Atualizado em {ticket.updatedAt.toLocaleDateString('pt-BR')}
                        </span>
                      </span>
                    </div>
                  </div>
                  <i className="ri-arrow-right-s-line text-gray-400 text-xl"></i>
                </div>
              </div>
            ))}
          </div>

          {filteredTickets.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-inbox-line text-gray-400 text-3xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum chamado encontrado</h3>
              <p className="text-gray-500">Não há chamados com este status.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
